<template>
  <section id="producto-main">
    <div class="container-fluid px-0">
      <div v-if="isLoading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
      <div v-else>
        <div class="back-link mb-3">
          <a href="#" @click.prevent="goBack" class="text-decoration-none">
            <i class="fas fa-arrow-left me-2"></i> Volver
          </a>
        </div>

        <!-- Photo + Data -->
        <div class="row g-4">
          <div class="col-lg-6">
            <!-- Image Gallery -->
            <div class="product-image-container">
              <div v-if="hasPromoDiscount" class="promo-badge-detail">
                <span class="promo-text">¡SUPER OFERTA!</span>
                <span class="promo-percentage">-{{product.discount_percentage}}%</span>
              </div>
              <div v-for="(photo, index) in allImages"
                   :key="index"
                   class="gallery-item"
                   :class="{'active': currentImageIndex === index}">
                <div class="image-wrapper">
                  <div class="image-loading" v-if="!imageLoaded[index]">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Cargando...</span>
                    </div>
                  </div>
                  <div class="image-error" v-if="imageError[index]">
                    <i class="fa fa-exclamation-circle"></i>
                    <span>Error al cargar la imagen</span>
                  </div>
                  <img :src="photo"
                       :alt="product.code_based"
                       @click="zoomImage(photo)"
                       :class="{'image-loaded': imageLoaded[index]}"
                       @load="onImageLoad(index)"
                       @error="onImageError(index)">
                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-6 sticky-header">
            <div class=" box-producto-detalle shadow-sm rounded">
              <div class="product-badge-agotado" v-if="product.stock_available <= 0">
                <span>AGOTADO</span>
              </div>

              <div class="">
                <span class="title-main">{{product.code_based}}</span>
                <h1 class="product-name">
                  {{product.description}}
                </h1>

                <div class="box-product-price">
                  <div v-if="hasDiscount" class="discount-info mb-2">
                    <span class="discount-badge">
                      <i class="fa fa-tag"></i> {{product.discount_percentage}}% OFF
                    </span>
                  </div>
                  <div class="price-display-detail">
                    <span v-if="hasDiscount" class="product-price-original">
                      {{product.pack != '1' ? product.price : 'PACK ' + product.price | currency}}
                      <small>+ IVA</small>
                    </span>
                    <span class="product-price h2" :class="{'price-with-discount': hasDiscount}">
                      {{product.pack != '1' ? finalPrice : 'PACK ' + finalPrice | currency}}
                      <small class="text-muted">+ IVA</small>
                    </span>
                  </div>
                  <div v-if="hasDiscount" class="savings-info">
                    Ahorrás: <strong>{{savings | currency}}</strong>
                  </div>
                </div>
              </div>

              <div class="box-cantidad" v-if="product.stock_available > 0">
                <div class="d-flex align-items-center gap-3 mb-3">
                  <div class="quantity-input-group">
                    <button
                      class="quantity-btn"
                      @click="decreaseQuantity"
                      :disabled="!canDecrease"
                      title="Disminuir cantidad"
                    >
                      <i class="fa fa-minus"></i>
                    </button>
                    <input
                      type="number"
                      class="quantity-input"
                      v-model.number="quantity"
                      @change="onQuantityInput"
                      :min="rangeStock[0]"
                      :max="rangeStock[rangeStock.length-1]"
                      step="1"
                      :title="'Cantidad: ' + quantity + ' unidades'"
                    >
                    <button
                      class="quantity-btn"
                      @click="increaseQuantity"
                      :disabled="!canIncrease"
                      title="Aumentar cantidad"
                    >
                      <i class="fa fa-plus"></i>
                    </button>
                  </div>

                  <button class="btn btn-primary btn-lg flex-grow-1 add-to-cart-btn"
                          @click.prevent="quickAddToCart(product)"
                          :disabled="!quantity">
                    <i class="fa fa-shopping-cart me-2"></i>
                    AÑADIR AL CARRITO
                  </button>
                </div>

                <div class="purchase-limits">
                  <div class="limit-item">
                    <i class="fa fa-info-circle"></i>
                    <span class="limit-label">Código:</span>
                    <span class="limit-value">{{product.code}}</span>
                  </div>
                  <div class="limit-item" v-if="product.product_purchase_min > 1">
                    <i class="fa fa-info-circle"></i>
                    <span class="limit-label">Mínimo de Compra:</span>
                    <span class="limit-value">{{product.product_purchase_min}} Unidades</span>
                  </div>
                  <div class="limit-item" v-if="product.product_purchase_max > 1 && product.product_purchase_max < product.STOCK">
                    <i class="fa fa-info-circle"></i>
                    <span class="limit-label">Máximo de Compra:</span>
                    <span class="limit-value">{{product.product_purchase_max}} Unidades</span>
                  </div>
                  <div class="limit-item" v-if="product.product_multiplier > 1">
                    <i class="fa fa-info-circle"></i>
                    <span class="limit-label">Múltiplo de Compra:</span>
                    <span class="limit-value">{{product.product_multiplier}} Unidades</span>
                  </div>
                  <div class="limit-item" v-if="product.stock_available > 0">
                    <i class="fa fa-cubes"></i>
                    <span class="limit-label">Stock Disponible:</span>
                    <span class="limit-value">{{product.stock_available}} Unidades</span>
                  </div>
                  <div class="limit-item" v-if="product.barcode !== ''">
                    <i class="fa fa-barcode"></i>
                    <span class="limit-label">EAN:</span>
                    <span class="limit-value">{{product.barcode}}</span>
                  </div>
                </div>
              </div>
              <div class="box-descripcion">
                <div class="box-data" v-if="product.content">
                  <h3 class="h5 mb-3 section-title">
                    <i class="fa fa-info-circle me-2"></i>
                    INFORMACIÓN DEL PRODUCTO
                  </h3>
                  <div class="box-info">
                    <div class="info-content" v-html="observacion"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
    import Api from '@/Api';
    import {baseUrl} from "../Api";

    export default {
      props:['product','goBack'],
        data(){
          return {
            productId:0,
            isLoading:false,
            quantity: 1,
            photos:[],
            allImages: [],
            currentImageIndex: 0,
            isZoomEnabled: true,
            imageLoaded: {},
            imageError: {}
          };
        },
        computed: {
          hasDiscount() {
            return this.product.enable_discount && this.product.discount_percentage > 0;
          },
          hasPromoDiscount() {
            return this.hasDiscount && this.product.discount_percentage >= 50;
          },
          finalPrice() {
            if (this.hasDiscount) {
              return this.product.price * (1 - this.product.discount_percentage / 100);
            }
            return this.product.price;
          },
          savings() {
            if (this.hasDiscount) {
              return this.product.price - this.finalPrice;
            }
            return 0;
          },
          rangeStock() {
            // Ensure all values are numbers and have sensible defaults
            const min = Number(this.product.product_purchase_min) > 0 ? Number(this.product.product_purchase_min) : 1;
            const max = Number(this.product.product_purchase_max) > 0 ? Number(this.product.product_purchase_max) : Number(this.product.stock_available) || 1;
            const step = Number(this.product.product_multiplier) > 0 ? Number(this.product.product_multiplier) : 1;

            // If min > max, return [min] (edge case)
            if (min > max) return [min];

            const result = [];
            for (let i = min; i <= max; i += step) {
              result.push(i);
            }
            // If max is not exactly on a step, but is greater than min, and divisible by step, include it
            if ((max - min) % step !== 0 && max > min && max <= Number(this.product.stock_available)) {
              if (!result.includes(max)) result.push(max);
            }
            return result;
          },
          canDecrease() {
            const allowed = this.rangeStock;
            return allowed.indexOf(this.quantity) > 0;
          },
          canIncrease() {
            const allowed = this.rangeStock;
            return allowed.indexOf(this.quantity) < allowed.length - 1;
          },
          observacion() {
            if (this.product.content) {
              return this.product.content.replace(/\n/g, "<br />");
            }
            return '';
          },
          cartItem() {
            return this.$store.state.cart.items.find(item => item.product.code === this.product.code);
          }
        },
        mounted(){
            this.initializeGallery();
            // Initialize quantity with cart quantity if available, otherwise use minimum purchase value
            if (this.cartItem) {
              this.quantity = this.cartItem.quantity;
            } else {
              const min = Number(this.product.product_purchase_min) > 0 ? Number(this.product.product_purchase_min) : 1;
              this.quantity = min;
            }
        },
        methods:{
          initializeGallery() {
            // Initialize images
            let filteredImages = [];
            let images = this.product.images.map(img => this.findImage(img.url));
            // Filter out invalid images and noimage.jpg
            filteredImages = images.filter(img => img && img !== '/static/img/noimage.jpg');
            if (filteredImages.length === 0) {
              filteredImages = ['/static/img/noimage.jpg']; // Fallback image
            }
            if (filteredImages.length > 1)
            {
              //remove the first image if
              filteredImages = filteredImages.filter((img, index) => {
                return index !== 0;
              });
            }

            this.allImages = filteredImages;
          },
          selectImage(index) {
            this.currentImageIndex = index;
          },
          zoomImage(imageUrl) {
            // Remove any existing modal
            const existingModal = document.querySelector('.image-zoom-modal');
            if (existingModal) {
                existingModal.remove();
            }

            // Create modal for zoomed image
            const modal = document.createElement('div');
            modal.className = 'image-zoom-modal';
            modal.innerHTML = `
                <div class="modal-content">
                    <span class="close-modal">&times;</span>
                    <img src="${imageUrl}" alt="Zoomed product image">
                </div>
            `;

            // Add modal to body
            document.body.appendChild(modal);
            document.body.style.overflow = 'hidden'; // Prevent background scrolling

            // Close modal on click
            const closeModal = () => {
                modal.remove();
                document.body.style.overflow = ''; // Restore scrolling
            };

            modal.addEventListener('click', (e) => {
                if (e.target === modal || e.target.className === 'close-modal') {
                    closeModal();
                }
            });

            // Close on escape key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    closeModal();
                }
            });
          },
          findImage(url) {
            if (!url || url === "") return null;
            return url;
          },
          imageExists(image_url,base){
              fetch(image_url)
              .then(res => {
                if (res.status===200)
                {

                this.photos.push({image:baseUrl + base});
                }

              }).catch(err => {

              });
          },
          quickAddToCart(product) {

            console.log(product);
            this.$store.dispatch("addToCart", {product: product, quantity: this.quantity}).then(prd => {

               this.$swal({
                  title: 'Producto agregado',
                  text: product.code_based + " agregado",
                  timer: 1200,
                  showCancelButton: false,
                  showConfirmButton: false,
                  type: 'success',
                }).then((result) => {

                })

            }).catch(err=>{
              this.$swal({
                title: 'Oops!',
                text: err,
                type: 'error',
              }).then((result) => {

              })
            });
          },
          decreaseQuantity() {
            const allowed = this.rangeStock;
            const idx = allowed.indexOf(this.quantity);
            if (idx > 0) {
              this.quantity = allowed[idx - 1];
            }
          },
          increaseQuantity() {
            const allowed = this.rangeStock;
            const idx = allowed.indexOf(this.quantity);
            if (idx < allowed.length - 1) {
              this.quantity = allowed[idx + 1];
            }
          },
          onQuantityInput() {
            const allowed = this.rangeStock;
            let val = Number(this.quantity);
            // Snap to closest allowed value
            let closest = allowed.reduce((prev, curr) =>
              Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev
            );
            this.quantity = closest;
          },
          onImageLoad(index) {
            this.$set(this.imageLoaded, index, true);
          },
          onImageError(index) {
            this.$set(this.imageError, index, true);
            this.$set(this.imageLoaded, index, true); // Mark as loaded to remove spinner
          },
        }
    }
</script>

<style scoped>
/* Loading spinner */
.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  vertical-align: text-bottom;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border .75s linear infinite;
}

@keyframes spinner-border {
  to { transform: rotate(360deg); }
}

.text-primary {
  color: #007bff !important;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.back-link {
  padding: 1rem;
}

.back-link a {
  color: #666;
  transition: all 0.3s ease;
  font-size: 1.3rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background-color: #f8f9fa;
}

.back-link a:hover {
  color: #333;
  background-color: #e9ecef;
  transform: translateX(-5px);
}

.back-link i {
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.back-link a:hover i {
  transform: translateX(-3px);
}

.product-image-container {
  position: relative;
  background-color: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.promo-badge-detail {
  position: absolute;
  top: 20px;
  left: 20px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: bold;
  box-shadow: 0 6px 12px rgba(238, 90, 111, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.3;
  animation: pulse 2s ease-in-out infinite;
  z-index: 10;
}

.promo-badge-detail .promo-text {
  font-size: 0.8em;
  letter-spacing: 1.5px;
}

.promo-badge-detail .promo-percentage {
  font-size: 1.5em;
  font-weight: 900;
  margin-top: 2px;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.gallery-item {
  position: relative;
  margin-bottom: 2rem;
}

.gallery-item:last-child {
  margin-bottom: 0;
}

.gallery-item .image-wrapper {
  position: relative;
  padding-top: 75%; /* 4:3 Aspect Ratio */
  background-color: #f8f9fa;
  overflow: hidden;
  border-radius: 12px;
}

.gallery-item img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: all 0.3s ease;
  opacity: 0;
  padding: 1rem;
}

.gallery-item img.image-loaded {
  opacity: 1;
}

.gallery-item img.zoom-enabled:hover {
  transform: scale(1.05);
  cursor: zoom-in;
}

/* Loading and error states */
.image-loading,
.image-error {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  z-index: 1;
}

.image-error {
  color: #dc3545;
  gap: 8px;
}

.image-error i {
  font-size: 2rem;
}

.image-error span {
  font-size: 0.9rem;
  font-weight: 500;
}

/* Product details box */
.box-producto-detalle {
  background: white;
  position: relative;
  height: fit-content;
}

/* Sticky header section */
.sticky-header {
  position: sticky;
  top: 0;
  background: white;
  padding: 1rem 0;
  z-index: 10;
  margin-bottom: 1rem;
}

.product-name {
  font-size: 2rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
}

.box-product-price {
  margin-top: 0.5rem;
}

.discount-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.discount-badge {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(238, 90, 111, 0.3);
}

.price-display-detail {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-price-original {
  color: #999;
  text-decoration: line-through;
  font-size: 1.2rem;
  font-weight: 400;
}

.product-price {
  color: #2c3e50;
  font-weight: 700;
}

.product-price.price-with-discount {
  color: #e74c3c;
}

.savings-info {
  margin-top: 8px;
  padding: 8px 12px;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  color: #155724;
  font-size: 0.95rem;
  display: inline-block;
}

.savings-info strong {
  color: #0f4220;
  font-size: 1.1em;
}

.product-badge-agotado {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #dc3545;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: bold;
}

.purchase-limits {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 12px;
  margin-top: 16px;
  border: 1px solid #e9ecef;
}

.limit-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 0.95rem;
  color: #495057;
}

.limit-item:not(:last-child) {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 8px;
}

.limit-item i {
  color: #6c757d;
  font-size: 1rem;
}

.limit-label {
  font-weight: 500;
  color: #495057;
}

.limit-value {
  color: #007bff;
  font-weight: 600;
}

.box-descripcion {
  color: #666;
}

.quantity-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8f9fa;
  padding: 8px;
  border-radius: 12px;
  width: fit-content;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  border: 1px solid #e9ecef;
}

.quantity-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #333;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.quantity-btn:hover:not(:disabled) {
  background: #007bff;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f8f9fa;
}

.quantity-input {
  width: 70px;
  height: 36px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 500;
  color: #333;
  background: white;
  -moz-appearance: textfield;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.05);
}

.quantity-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0,123,255,0.1);
}

.quantity-input::-webkit-outer-spin-button,
.quantity-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.add-to-cart-btn {
  height: 48px;
  font-weight: 600;
  font-size: 1.1rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  margin-top: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.add-to-cart-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.add-to-cart-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.product-code {
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.code-label {
  color: #6c757d;
  font-size: 0.9rem;
  margin-right: 8px;
}

.code-value {
  color: #495057;
  font-weight: 600;
  font-family: monospace;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
}

.product-title {
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.title-main {
  color: #2c3e50;
  font-weight: 600;
  font-size: 1.25rem;
  display: block;
}

.title-description {
  color: #6c757d;
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
}

.section-title {
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.section-title i {
  color: #007bff;
}

.box-info {
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 16px;
}

.info-content {
  color: #495057;
  line-height: 1.6;
  font-size: 0.95rem;
}

.info-content :deep(p) {
  margin-bottom: 1rem;
}

.info-content :deep(ul),
.info-content :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.info-content :deep(li) {
  margin-bottom: 0.5rem;
}

.info-content :deep(strong) {
  color: #2c3e50;
  font-weight: 600;
}

hr {
  border-color: #e9ecef;
  opacity: 0.5;
  margin: 2rem 0;
}
</style>




// WEBPACK FOOTER //
// src/components/ProductDetailComponent.vue